/**
 * `ModalShell` — the shared chrome for the page's modals: a blurred scrim, a
 * centered dialog box, ESC-to-close, backdrop-click-to-close, body scroll
 * lock, and initial focus. Extracted from `BucketModal` so the new
 * `LagDetailModal` reuses the exact same proven behavior instead of
 * re-implementing focus/scroll/escape handling.
 *
 * The shell owns ONLY the wrapper; callers render their own header + body as
 * `children`. Mount the shell conditionally (it has no `isOpen` prop) — being
 * mounted means open.
 *
 * Design notes (DESIGN.md §16): the scrim uses `--scrim` (dark in both
 * themes), never `ink-1/40` which would invert white in dark mode. Entrance
 * uses the CSS keyframes `modal-fade` / `modal-pop` defined in `index.css`.
 */
import clsx from 'clsx';
import { useEffect, useRef, type ReactNode, type RefObject } from 'react';

export interface ModalShellProps {
  onClose: () => void;
  /** `id` of the heading element inside, wired to `aria-labelledby`. */
  labelledById?: string;
  /** Tailwind max-width for the dialog box (default `max-w-md`). */
  maxWidthClass?: string;
  /** Element to focus on open. Falls back to the dialog box itself. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

export function ModalShell({
  onClose,
  labelledById,
  maxWidthClass = 'max-w-md',
  initialFocusRef,
  children,
}: ModalShellProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Only close when a click both starts and ends on the scrim — a drag that
  // begins inside the dialog (text selection, scrollbar) and releases over
  // the scrim must not dismiss the modal.
  const downOnScrim = useRef(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Stop a parent ESC handler (e.g. a page-level shortcut) from also
        // firing — the modal owns Escape while it's open.
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        // Trap Tab/Shift+Tab inside the dialog so keyboard focus cannot
        // escape behind the scrim — required to honour `aria-modal="true"`.
        const dialog = dialogRef.current;
        if (dialog == null) return;
        const focusable = dialog.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) {
          // Nothing focusable inside — keep focus on the dialog itself.
          e.preventDefault();
          dialog.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === dialog)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    const prevFocus = document.activeElement as HTMLElement | null;
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    (initialFocusRef?.current ?? dialogRef.current)?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus();
    };
  }, [onClose, initialFocusRef]);

  return (
    <div
      className={clsx(
        'fixed inset-0 z-50 flex items-center justify-center p-4',
        'bg-[rgb(var(--scrim)/0.5)] backdrop-blur-[2px]',
        'animate-[modal-fade_120ms_ease-out]',
      )}
      onMouseDown={(e) => {
        downOnScrim.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (downOnScrim.current && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
        }}
        className={clsx(
          'relative flex max-h-[85vh] w-full flex-col overflow-hidden outline-none',
          'rounded-lg border border-line bg-paper-1 shadow-pop',
          'animate-[modal-pop_140ms_ease-out]',
          maxWidthClass,
        )}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * The shared close (✕) button used in modal headers. Matches `BucketModal`'s
 * original control so both modals look identical.
 */
export function ModalCloseButton({
  onClose,
  buttonRef,
}: {
  onClose: () => void;
  buttonRef?: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClose}
      className="-m-1.5 rounded p-1.5 text-ink-3 transition-colors hover:bg-paper-2 hover:text-ink-1"
      aria-label="Close"
    >
      <svg
        viewBox="0 0 16 16"
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
      >
        <path d="M3 3l10 10M13 3L3 13" />
      </svg>
    </button>
  );
}
